import React, { ReactNode, useEffect } from 'react';

import { PrimaryButton, TextButton } from '@components/Button/variants';
import IconClose from '@components/Images/Rebranding/IconClose';
import TIModal from '@components/TIModal';
import useEventTracking from '@hooks/useEventTracking';
import { ClickTarget, ImpressionElement, click, impression } from '@tracking';

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  isLoading?: boolean;
  title: string;
  description: string;
  confirmText: string;
  confirmingText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel?: () => void;
  hideCancel?: boolean;
  rightIcon?: ReactNode;
  customButton?: ReactNode;
  additionalText?: ReactNode;
  impressionElement?: ImpressionElement;
  confirmClickTarget?: ClickTarget;
  cancelClickTarget?: ClickTarget;
}

const ConfirmationModal = ({
  additionalText,
  cancelClickTarget,
  cancelText = 'Cancel',
  confirmClickTarget,
  confirmingText = 'Processing…',
  confirmText,
  customButton,
  description,
  hideCancel = false,
  impressionElement,
  isLoading = false,
  isOpen,
  onCancel,
  onClose,
  onConfirm,
  rightIcon,
  title,
}: ConfirmationModalProps) => {
  const { trackEvent } = useEventTracking();

  const handleCancelClick = () => {
    if (onCancel) {
      onCancel();
    } else {
      onClose();
    }
    if (cancelClickTarget) {
      trackEvent(click({ clickTarget: cancelClickTarget, clickText: cancelText }));
    }
  };

  const handleConfirmClick = () => {
    onConfirm();
    if (confirmClickTarget) {
      trackEvent(click({ clickTarget: confirmClickTarget, clickText: confirmText }));
    }
  };

  const handleModalClose = () => {
    trackEvent(click({ clickTarget: ClickTarget.Close }));
    onClose();
  };

  useEffect(() => {
    if (isOpen && impressionElement) {
      trackEvent(impression({ impressionElement }));
    }
  }, [isOpen, trackEvent, impressionElement]);

  return (
    <TIModal isOpen={isOpen} onClose={handleModalClose} customHeader>
      <div className="flex w-[calc(100vw_-_64px)] flex-nowrap justify-between gap-x-36 font-suisse-intl xs:w-[377px] sm:w-[547px]">
        <div>
          <h2 className="mb-0 mt-16 font-suisse-works text-headline-xs">{title}</h2>
          <p className="mb-0 mt-16">{description}</p>
          {additionalText && <p className="peer mb-0 mt-12">{additionalText}</p>}
          <div className="my-16 flex items-center justify-between gap-x-24 sm:mt-32">
            {customButton ? (
              customButton
            ) : (
              <PrimaryButton
                as="button"
                onClick={handleConfirmClick}
                className={`!w-3/4 flex-shrink-0 ${
                  isLoading ? ' pointer-events-none opacity-50' : ''
                }`}
                disabled={isLoading}
              >
                {isLoading ? confirmingText : confirmText}
              </PrimaryButton>
            )}
            {!hideCancel && (
              <TextButton
                as="button"
                onClick={handleCancelClick}
                className="cursor-pointer !text-grayscale-gray underline hover:!text-grayscale-gray/50"
                disabled={isLoading}
              >
                {cancelText}
              </TextButton>
            )}
          </div>
        </div>
        {rightIcon && (
          <div className="pointer-events-none -mr-16 hidden w-[185px] flex-shrink-0 items-center justify-center bg-grayscale-charcoal sm:flex">
            {rightIcon}
          </div>
        )}
        <button
          className="absolute -right-4 top-16 touch-manipulation border-0 bg-transparent"
          onClick={handleModalClose}
          type="button"
          aria-label="Close this modal"
        >
          <IconClose className="text-grayscale-charcoal sm:text-white" height={26} width={26} />
        </button>
      </div>
    </TIModal>
  );
};

export default ConfirmationModal;
